import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import { formatNaira } from '../store/slices/currencySlice';
import { useAppSelector } from '../store/hooks';
import { selectCurrentUser } from '../store/slices/authSlice';
import './Checkout.css';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderId = location.state?.orderId;
  const orderNumber = location.state?.orderNumber;
  const total = location.state?.total;
  const currentUser = useAppSelector(selectCurrentUser);

  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptUploadError, setReceiptUploadError] = useState('');
  const [receiptUploaded, setReceiptUploaded] = useState(false);

  useEffect(() => {
    if (!orderId) {
      navigate('/my-orders');
      return;
    }

    const fetchPaymentInfo = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get('/payment-information');
        setPaymentInfo(response.data);
      } catch (err) {
        console.error('Failed to load payment information', err);
        setError(err.response?.data?.message || 'Failed to load payment information.');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentInfo();
  }, [orderId, navigate]);

  const formatLocalPhone = (phone) => {
    if (!phone) return '';
    let p = phone.trim();
    if (p.startsWith('+234')) {
      return '0' + p.slice(4);
    }
    if (p.startsWith('234')) {
      return '0' + p.slice(3);
    }
    return p;
  };

  const referenceRaw = currentUser?.phone || currentUser?.email || orderNumber || `#${orderId}`;
  const reference = currentUser?.phone ? formatLocalPhone(currentUser.phone) : referenceRaw;

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.error('Failed to copy text', e);
    }
  };

  const handleReceiptUpload = async (e) => {
    e.preventDefault();
    if (!receiptFile || !orderId) return;
    setReceiptUploadError('');
    setReceiptUploading(true);
    try {
      const formData = new FormData();
      formData.append('receipt', receiptFile);
      await api.post(`/orders/${orderId}/receipt`, formData);
      setReceiptUploaded(true);
      setReceiptFile(null);
    } catch (err) {
      setReceiptUploadError(err.response?.data?.message || 'Failed to upload receipt.');
    } finally {
      setReceiptUploading(false);
    }
  };

  return (
    <Layout>
      <div className="checkout-page">
        <div className="checkout-container">
          <h1>Checkout</h1>

          <section className="checkout-section">
            <h2>Order Summary</h2>
            <p><strong>Order:</strong> {orderNumber || `#${orderId}`}</p>
            <p><strong>Total Amount:</strong> {formatNaira(total)}</p>
          </section>

          <section className="checkout-section">
            <h2>Payment Information</h2>
            {loading ? (
              <p>Loading payment details...</p>
            ) : error ? (
              <p className="checkout-error">{error}</p>
            ) : !paymentInfo ? (
              <p className="checkout-error">Payment information is not available at the moment.</p>
            ) : (
              <div className="checkout-payment-card">
                <div className="checkout-row">
                  <span>Account Name</span>
                  <div className="checkout-row-value">
                    <strong>{paymentInfo.account_name}</strong>
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() => handleCopy(paymentInfo.account_name)}
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div className="checkout-row">
                  <span>Account Number</span>
                  <div className="checkout-row-value">
                    <strong>{paymentInfo.account_number}</strong>
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() => handleCopy(paymentInfo.account_number)}
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div className="checkout-row">
                  <span>Bank Name</span>
                  <div className="checkout-row-value">
                    <strong>{paymentInfo.bank_name}</strong>
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() => handleCopy(paymentInfo.bank_name)}
                    >
                      Copy
                    </button>
                  </div>
                </div>
                {reference && (
                  <div className="checkout-row">
                    <span>Reference</span>
                    <div className="checkout-row-value">
                      <strong>{reference}</strong>
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => handleCopy(reference)}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}
                <p className="checkout-note">
                  Please make a transfer to the account above and <strong>include your reference</strong>{' '}
                  (your phone number: <strong>{reference}</strong>) in the payment narration. Including this reference
                  helps us match your payment quickly and speed up processing of your order.
                </p>

                <div className="checkout-receipt-section">
                  <h3>Upload payment receipt (optional)</h3>
                  <p className="checkout-receipt-hint">You can upload a screenshot or photo of your payment confirmation to speed up order processing.</p>
                  {receiptUploaded ? (
                    <p className="checkout-receipt-success">Receipt uploaded successfully.</p>
                  ) : (
                    <form onSubmit={handleReceiptUpload} className="checkout-receipt-form">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                        onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                        className="checkout-receipt-input"
                      />
                      <button
                        type="submit"
                        className="checkout-receipt-btn"
                        disabled={!receiptFile || receiptUploading}
                      >
                        {receiptUploading ? 'Uploading...' : 'Upload receipt'}
                      </button>
                    </form>
                  )}
                  {receiptUploadError && <p className="checkout-error">{receiptUploadError}</p>}
                </div>

                <div className="checkout-done-container">
                  <button
                    type="button"
                    className="checkout-done-btn"
                    onClick={() => navigate('/my-orders')}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default Checkout;

